import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@zg-rentals/react-query5';

import { cloneDeep } from 'es-toolkit';

import { RENTAL_MANAGER_API_V1_PREFIX, RENTAL_MANAGER_API_V2_PREFIX } from '../../constants/RouteConstants';
import { PropertiesSelectors } from '../../selectors';
import PropertiesActions from '../../actions/PropertiesActions';
import StringConstants from '../../constants/StringConstants';
import makeRequest from '../../utils/makeRequestUtils';
import useRequestConfig from '../../hooks/useRequestConfig';

import type { Dispatch, SetStateAction } from 'react';
import type { PhotoProps } from '@zg-rentals/maintenance-utils';

const useDeletePhoto = (
  discriminatoryContentErrors: Array<{ photoId: string; caption: string }>,
  setDiscriminatoryContentErrors: Dispatch<SetStateAction<Array<{ photoId: string; caption: string }>>>,
  queryOptions = {},
) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();
  const { photos } = useSelector((state) => PropertiesSelectors.makeCurrentProperty(state));

  const queryFn = (photoId: string) =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/photos/delete`,
      requestConfig,
      config: { params: { photoId } },
    });

  return useMutation({
    mutationFn: async (photoId: string) => {
      const data = await queryFn(photoId);
      const indexOfDeletedPhoto = photos.findIndex((photo: PhotoProps) => photo.id === photoId);
      const photosCopy = cloneDeep(photos);
      photosCopy.splice(indexOfDeletedPhoto, 1);

      const copyDiscriminatoryContent = cloneDeep(discriminatoryContentErrors);
      const errorIndexToUpdate = copyDiscriminatoryContent.findIndex((error) => error.photoId === photoId);
      copyDiscriminatoryContent.splice(errorIndexToUpdate, 1);

      setDiscriminatoryContentErrors(copyDiscriminatoryContent);
      dispatch(PropertiesActions.updateCurrentProperty({ photos: photosCopy }));

      return data;
    },
    ...queryOptions,
  });
};

const useSortPhoto = (propertyId: string, queryOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig(StringConstants.POST);

  const queryFn = (photosArray: Array<PhotoProps>) =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V2_PREFIX}/users/properties/photos/rank`,
      requestConfig,
      config: { params: { propertyId }, data: { ranks: photosArray } },
    });

  return useMutation({ mutationFn: (photosArray: Array<PhotoProps>) => queryFn(photosArray), ...queryOptions });
};

const useUpdatePhotoCaption = (queryOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();
  const { photos } = useSelector((state) => PropertiesSelectors.makeCurrentProperty(state));

  const queryFn = ({ photoId, caption }: { photoId: string; caption: string }) =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/photos/caption`,
      requestConfig,
      config: { params: { photoId, caption } },
    });

  return useMutation({
    mutationFn: async (photoData: { photoId: string; caption: string }) => {
      const data = await queryFn(photoData);
      const indexOfPhotoCaption = photos.findIndex((photo: PhotoProps) => photo.id === photoData.photoId);
      const photosCopy = cloneDeep(photos);
      photosCopy[indexOfPhotoCaption].caption = photoData.caption;

      dispatch(PropertiesActions.updateCurrentProperty({ photos: photosCopy }));

      return data;
    },
    ...queryOptions,
  });
};

export { useDeletePhoto, useSortPhoto, useUpdatePhotoCaption };
